/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/attendanceModern/screens/ChecklistScreen.tsx
 * PURPOSE: SC-017-EMP-ATTENDANCE [8] checklist — 3 tab (Đầu ca/Giữa ca/Kết ca),
 *          gate "Chấm công ra" chỉ mở khi checklist kết ca đủ mục bắt buộc.
 *          Web trước đây KHÔNG có tính năng này — 0% tích hợp với backend
 *          checklist (attendance_sprint72.py), dù backend đã sẵn sàng 100%.
 *          2026-09-04 (structural layout parity, root causes #1/#2): removed
 *          the `.card` wrapper + duplicate in-body title+close header —
 *          AttendanceModern.tsx's page header is now state-driven and
 *          supplies "Checklist ca" + a back-to-home button for this screen
 *          (mirrors mobile's headerConfig.checklist.onBack → goHome). Dropped
 *          the now-redundant `onClose` prop. Also: (a) added the checked-item
 *          ✓ pill badge mobile's ListItem shows (`pill={item.is_checked ?
 *          <Pill label="✓".../> : undefined}`, AttendanceScreen.tsx:1015-1042)
 *          — web only had the toggle button, no separate badge; (b) a
 *          checklist-status load error no longer replaces the whole item list
 *          with bare error text — it now layers a Banner-style message
 *          alongside the (possibly stale/empty) list, matching mobile's
 *          `hook.error ? <Banner variant="error">...</Banner> : null` placed
 *          AFTER the item map, never as an early return.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02
 * COMPLIANCE: SDLC 6.5.0 + WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE [8]
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE
 * - Backend: backend/app/api/v2/endpoints/attendance_sprint72.py (5 checklist endpoints)
 * - Mobile source of truth: mobile/src/screens/attendance/AttendanceScreen.tsx renderChecklist (:1024-1082)
 *  */

import React, { useCallback, useEffect, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { attendanceApi, type ChecklistStatusResponse } from '@/services/api/attendance';
import { useAuthStore } from '@/store/auth';
import styles from '@/pages/AttendanceModern.module.css';

type ShiftType = 'PRE_SHIFT' | 'POST_SHIFT';

interface ChecklistPhaseProps {
  attendanceId: string;
  shiftType: ShiftType;
  /** Kết ca cần biết mandatory đủ chưa để mở gate — Đầu ca không cần. */
  onStatusChange?: (status: ChecklistStatusResponse | null) => void;
}

const ChecklistPhase: React.FC<ChecklistPhaseProps> = ({ attendanceId, shiftType, onStatusChange }) => {
  const { accessToken } = useAuthStore();
  const [status, setStatus] = useState<ChecklistStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isActivating, setIsActivating] = useState(false);
  const [isTicking, setIsTicking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await attendanceApi.getChecklistStatus(accessToken, attendanceId, shiftType);
      setStatus(result);
      onStatusChange?.(result);
    } catch {
      // Checklist hardening pass (#742, 2026-09-02) — lỗi thật (không phải
      // "chưa activate", đã trả null riêng ở lớp API) phải báo, không nuốt.
      setError('Không tải được checklist. Vui lòng thử lại.');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, attendanceId, shiftType]);

  useEffect(() => {
    load();
  }, [load]);

  const handleActivate = async () => {
    if (!accessToken) return;
    setIsActivating(true);
    setError(null);
    try {
      await attendanceApi.activateChecklist(accessToken, attendanceId, shiftType);
      await load();
    } catch {
      setError('Không kích hoạt được checklist. Vui lòng thử lại.');
    } finally {
      setIsActivating(false);
    }
  };

  const handleTick = async (itemId: string, isChecked: boolean) => {
    if (!accessToken) return;
    setIsTicking(true);
    setError(null);
    try {
      await attendanceApi.tickChecklistItem(accessToken, attendanceId, { item_id: itemId, is_checked: isChecked });
      await load();
    } catch {
      setError('Không cập nhật được mục checklist. Vui lòng thử lại.');
    } finally {
      setIsTicking(false);
    }
  };

  if (isLoading) return <p className={styles.cardDescription}>Đang tải checklist...</p>;

  // Checklist hardening pass (#742) + 2026-09-04 structural-parity fix: a load
  // error used to be an early return that REPLACED the entire view (list
  // gone). Mobile layers its error Banner alongside the (possibly stale)
  // item list / activate button instead — matches that here.
  const errorBanner = error ? (
    <p className={`${styles.statusMessage} ${styles.statusError}`} role="alert" data-testid={`checklist-error-${shiftType}`}>
      {error}
    </p>
  ) : null;

  if (!status) {
    return (
      <>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={handleActivate}
          disabled={isActivating}
          data-testid={`checklist-activate-${shiftType}`}
        >
          {isActivating ? 'Đang kích hoạt...' : 'Checklist ca'}
        </button>
        {errorBanner}
      </>
    );
  }

  return (
    <div>
      {status.items.map((item) => (
        <div key={item.item_id} className={styles.actionsRow} style={{ justifyContent: 'space-between' }}>
          <span>{item.title_vi}</span>
          <div className={styles.checklistItemActions}>
            {item.is_checked && (
              <span className={styles.taskPill} data-testid={`checklist-item-pill-${item.item_id}`}>✓</span>
            )}
            <button
              type="button"
              className={item.is_checked ? styles.secondaryButton : styles.primaryButton}
              onClick={() => handleTick(item.item_id, !item.is_checked)}
              disabled={isTicking}
              data-testid={`checklist-item-${item.item_id}`}
            >
              {item.is_checked ? '✓' : '○'}
            </button>
          </div>
        </div>
      ))}
      {errorBanner}
      <Progress value={status.progress.percentage} data-testid={`checklist-progress-${shiftType}`} />
    </div>
  );
};

interface ChecklistScreenProps {
  attendanceId: string;
  onClockOut: () => void;
}

export const ChecklistScreen: React.FC<ChecklistScreenProps> = ({ attendanceId, onClockOut }) => {
  const [postShiftStatus, setPostShiftStatus] = useState<ChecklistStatusResponse | null>(null);
  const [showLockedSheet, setShowLockedSheet] = useState<'handover' | 'complaint' | null>(null);

  // GATE nghiệp vụ (khớp mobile canClockOutFromChecklist): nút "Chấm công ra"
  // ở tab Kết ca CHỈ hiện/bật khi mandatory_total > 0 && mandatory_completed === mandatory_total.
  const canClockOut =
    !!postShiftStatus &&
    postShiftStatus.progress.mandatory_total > 0 &&
    postShiftStatus.progress.mandatory_completed === postShiftStatus.progress.mandatory_total;

  return (
    <>
      <Tabs defaultValue="pre">
        <TabsList>
          <TabsTrigger value="pre" data-testid="checklist-tab-pre">Đầu ca</TabsTrigger>
          <TabsTrigger value="mid" data-testid="checklist-tab-mid">Giữa ca</TabsTrigger>
          <TabsTrigger value="post" data-testid="checklist-tab-post">Kết ca</TabsTrigger>
        </TabsList>

        <TabsContent value="pre">
          <ChecklistPhase attendanceId={attendanceId} shiftType="PRE_SHIFT" />
        </TabsContent>

        {/* Giữa ca — CHỈ 1 dòng SOP tĩnh, KHÔNG tick được, KHÔNG gọi API. */}
        <TabsContent value="mid">
          <p className={styles.cardDescription}>Chỉ xem SOP — không tick.</p>
        </TabsContent>

        <TabsContent value="post">
          <ChecklistPhase attendanceId={attendanceId} shiftType="POST_SHIFT" onStatusChange={setPostShiftStatus} />
          {canClockOut ? (
            <button type="button" className={styles.primaryButton} onClick={onClockOut} data-testid="checklist-clock-out-btn">
              Chấm công ra
            </button>
          ) : (
            <p className={styles.cardDescription}>Hoàn tất checklist kết ca để mở nút Chấm công ra.</p>
          )}
          <div className={styles.actionsRow}>
            <button type="button" className={styles.secondaryButton} onClick={() => setShowLockedSheet('handover')} data-testid="checklist-handover-btn">
              Bàn giao ca
            </button>
            <button type="button" className={styles.secondaryButton} onClick={() => setShowLockedSheet('complaint')} data-testid="checklist-complaint-btn">
              Gửi khiếu nại
            </button>
          </div>
        </TabsContent>
      </Tabs>

      {showLockedSheet && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowLockedSheet(null)}>
          <div className={styles.card} style={{ maxWidth: 360 }} onClick={(e) => e.stopPropagation()}>
            <div className={styles.headerContent}>
              <h2 className={styles.cardTitle}>{showLockedSheet === 'handover' ? 'Bàn giao ca' : 'Gửi khiếu nại'}</h2>
              <button type="button" className={styles.backButton} onClick={() => setShowLockedSheet(null)}>
                ✕
              </button>
            </div>
            <p className={styles.cardDescription}>Tính năng chưa sẵn sàng</p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChecklistScreen;
